import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import FilterPopover from '../../residents/components/FilterPopover.jsx';
import BuildingEmptyState from './BuildingEmptyState.jsx';

/**
 * Column definitions for the buildings table.
 */
export const BUILDING_COLUMN_DEFS = [
  { key: 'building_name', label: 'Building Name', sortable: true, filterable: true, required: true },
  { key: 'street_address', label: 'Street Address', sortable: true, filterable: false, required: false },
  { key: 'city', label: 'City', sortable: true, filterable: true, required: false },
  { key: 'state', label: 'State', sortable: true, filterable: true, required: false },
  { key: 'zipcode', label: 'Zip Code', sortable: true, filterable: false, required: false },
  { key: 'country', label: 'Country', sortable: true, filterable: true, required: false },
  { key: 'area_count', label: 'Areas', sortable: true, filterable: false, required: true },
];

/**
 * BuildingsTable — data table for buildings with sorting, filtering,
 * sticky header, row selection, and row actions.
 */
const BuildingsTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onEditBuilding,
  onDeleteBuilding,
  onAddBuilding,
  onClearFilters,
}) => {
  const activeColumns = useMemo(
    () => BUILDING_COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = useCallback(
    (columnKey) => {
      const values = new Set();
      (allData || data).forEach((row) => {
        const val = row[columnKey];
        values.add(val != null && val !== '' ? String(val) : '(empty)');
      });
      return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    },
    [allData, data]
  );

  const allSelected = data.length > 0 && data.every((r) => selectedIds.includes(r.building_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'building_name':
        return (
          <span className="ap-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            {row.building_name}
          </span>
        );

      case 'street_address':
        return row.street_address ? (
          <span className="ap-cell-text">{row.street_address}</span>
        ) : (
          <span className="ap-cell-text--muted">—</span>
        );

      case 'city':
        return row.city ? (
          <span className="ap-cell-text">{row.city}</span>
        ) : (
          <span className="ap-cell-text--muted">—</span>
        );

      case 'state':
        return row.state ? (
          <span className="ap-cell-text">{row.state}</span>
        ) : (
          <span className="ap-cell-text--muted">—</span>
        );

      case 'zipcode':
        return row.zipcode ? (
          <span className="ap-cell-text">{row.zipcode}</span>
        ) : (
          <span className="ap-cell-text--muted">—</span>
        );

      case 'country':
        return row.country ? (
          <span className="ap-cell-text">{row.country}</span>
        ) : (
          <span className="ap-cell-text--muted">—</span>
        );

      case 'area_count': {
        const areas = row.areas || [];
        if (areas.length === 0) return <span className="ap-cell-text--muted">None</span>;
        return <AreasPopoverCell areas={areas} />;
      }

      default:
        return <span className="ap-cell-text">{row[columnKey] || '—'}</span>;
    }
  };

  if (data.length === 0) {
    const hasAnyData = (allData || []).length > 0;
    return (
      <BuildingEmptyState
        type={hasAnyData ? 'no-results' : 'no-data'}
        onAddBuilding={hasAnyData ? undefined : onAddBuilding}
        onClearFilters={hasAnyData ? onClearFilters : undefined}
      />
    );
  }

  return (
    <div className="ap-table-wrap">
      <table className="ap-table">
        <thead className="ap-thead">
          <tr>
            {/* Checkbox column */}
            <th scope="col" className="ap-th ap-th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={onSelectAll}
                className="ap-checkbox"
                aria-label="Select all buildings"
              />
            </th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="ap-th">
                <div className="ap-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="ap-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.key}
                      label={col.label}
                      values={getFilterValues(col.key)}
                      selectedValues={filters[col.key] || []}
                      onChange={onFilterChange}
                    />
                  ) : null}
                </div>
              </th>
            ))}

            {/* Actions column */}
            <th scope="col" className="ap-th ap-th--actions">
              <span className="ap-sr-only">Actions</span>
            </th>
          </tr>
        </thead>

        <tbody className="ap-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.building_id);
            return (
              <tr
                key={row.building_id}
                className={`ap-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                <td className="ap-td ap-td--checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelectRow(row.building_id)}
                    className="ap-checkbox"
                    aria-label={`Select ${row.building_name}`}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="ap-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}

                {/* Row actions kebab */}
                <td className="ap-td ap-td--actions">
                  <BuildingRowActions
                    building={row}
                    onEdit={onEditBuilding}
                    onDelete={onDeleteBuilding}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * AreasPopoverCell — inline popover showing area names in a building.
 */
const AreasPopoverCell = ({ areas }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="ap-areas-cell" ref={ref}>
      <button
        type="button"
        className="ap-areas-badge"
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        aria-expanded={open}
        aria-label={`${areas.length} area${areas.length !== 1 ? 's' : ''} in building`}
      >
        <i className="fe fe-grid" style={{ fontSize: '12px' }} aria-hidden="true" />
        {areas.length}
      </button>
      {open ? (
        <div className="ap-areas-popover">
          <div className="ap-areas-popover__title">Areas ({areas.length})</div>
          <ul className="ap-areas-popover__list">
            {areas.map((a, i) => (
              <li key={a.area_id || i} className="ap-areas-popover__item">
                <span className="ap-areas-popover__name">{a.area_name}</span>
                {a.area_type_description ? (
                  <span className="ap-areas-popover__type">{a.area_type_description}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

/**
 * BuildingRowActions — kebab menu for per-row actions.
 */
const BuildingRowActions = ({ building, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="ap-row-actions" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="ap-row-actions__trigger"
        aria-label={`Actions for ${building.building_name}`}
        aria-expanded={open}
      >
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>

      {open ? (
        <div className="ap-row-actions__menu">
          <button
            type="button"
            onClick={() => { onEdit(building); setOpen(false); }}
            className="ap-row-actions__item"
          >
            <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
            Edit Building
          </button>
          <button
            type="button"
            onClick={() => { onDelete(building); setOpen(false); }}
            className="ap-row-actions__item ap-row-actions__item--danger"
          >
            <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
            Delete Building
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default BuildingsTable;
